// Third party
import React, { CSSProperties } from 'react';
import classNames from 'classnames';

// Components
import ComparisonCardContent from './ComparisonCardContent';
import ComparisonFXItem from './ComparisonFXItem';
import EditBenchmark from '@/views/components/edit-benchmark/EditBenchmark';
import ExpandedPortfolio from './ExpandedPortfolio';

// Types
import Alt from '@/types/models/alt';
import Benchmark from '@/types/benchmark';
import BenchmarkPortfolio from '@/types/benchmark-portfolio';
import Comparison from '@/types/comparison';
import ComparisonCategory from '@/types/comparison-category';
import ComparisonField from '@/types/comparison-field';
import ComparisonHolding from '@/types/comparison-holding';
import ComparisonItem from '@/types/comparison-item';
import ComparisonItemCategory from '@/types/comparison-item-category';
import ComparisonItemGroup from '@/types/comparison-item-group';
import Fund from '@/types/fund';
import Locale from '@/types/locale';
import Nullable from '@/types/nullable';
import Portfolio from '@/types/portfolio';
import RiskPeriods from '@/types/risk-periods';
import SelectedCategories from '@/types/selected-categories';
import Stock from '@/types/stock';
import User from '@/types/user';

// Utils
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { enumerateArray } from '@/utils/objects';
import { formatMessage } from '@/utils/i18n';
import { isBenchmark } from '@/utils/comparison';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled } from '@/utils/permissions';

interface Props {
  className?: string;
  item: ComparisonItem;
  user?: User | null;
  readOnly?: boolean;
  comparison: Comparison;
  disableSaving?: boolean;
  editedPortfolio?: Portfolio;
  onItemRemove: (item: ComparisonItem) => void;
  onModifyPortfolio: (item: ComparisonItem) => void;
  onViewPortfolio: (item: ComparisonItem) => void;
  addCategoryBenchmark: (item: ComparisonItem) => void;
  onClonePortfolio: (item: ComparisonItem) => void;
  onCloneBenchmark: (item: ComparisonItem) => void;
  onSavePortfolio: (item: ComparisonItem, toContact?: boolean) => void;
  onModifyBenchmark: (item: ComparisonItem) => void;
  categories: ComparisonCategory[];
  editBenchmarkMode: boolean;
  editedBenchmark?: BenchmarkPortfolio;
  pinnedEditMode: boolean;
  onSelectItem: (item: ComparisonItem) => void;
  onCellClick: (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField) => void;
  isSelected: boolean;
  markupMode?: boolean;
  viewMarkup: boolean;
  previewMarkup?: boolean;
  markupTool?: string;
  selectedCategories?: SelectedCategories;
  riskPeriod: RiskPeriods;
  setMarkupComment: (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField, comment: string) => void;
  postMarkupComment: (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField, dotNumber: number, comment: string) => void;
  getNewDotNumber: () => number;
  clearMarkup: (item: ComparisonItem, categoryKey: string, fieldKey: string, markupTool: string) => void;
  updateTableHeight: (height: number) => void;
  resizeCommentLinesContainer: () => void;
  onShowCorrelation: (comparisonItem: ComparisonItem) => void;
  getItemHoldingsData: (holdings: ComparisonHolding[]) => Promise<{ alts: Alt[]; funds: Fund[]; stocks: Stock[]; benchmarks: Benchmark[] }>;
  onExpandPortfolioItem: (item: ComparisonItem, expand: boolean) => void;
  onToggleFXItem: (item: ComparisonItem, toggled: boolean) => void;
  getAdditionalCategoryStyles: (category: ComparisonCategory) => CSSProperties;
  disableStickyHeader?: boolean;
  onSaveBenchmark: (benchmarkPortfolio: BenchmarkPortfolio) => Promise<void>;
  onEditBenchmarkCancel: (benchmarkPortfolio: BenchmarkPortfolio) => void;
  updateComparisonItemHolding: (item: ComparisonItem, holdingId: string, holdingData: Partial<ComparisonHolding>) => void;
  updateComparisonItemColor: (item: ComparisonItem, color: string) => void;
  updateComparisonItemCategory?: (item: ComparisonItem, category: ComparisonItemCategory | undefined) => void;
  popoverContainerRef: Nullable<HTMLDivElement>;
  scrollbarContainerRef?: HTMLElement;
  hasStickyHeader?: boolean;
  onAddItemToGroup: (item: ComparisonItem, group: ComparisonItemGroup, value: number, fee?: number) => void;
  onRemoveItemFromGroup: (item: ComparisonItem, group: ComparisonItemGroup) => void;
  onModifyGroup: (item: ComparisonItem) => void;
  showGroups?: boolean;
  editGroups?: boolean;
  locale?: Locale;
}

const getSharingLabel = (user: User, item: ComparisonItem) => {
  if (item.providedBy && item.providedBy.id !== user.id) {
    if (user && userHasPermission(user, PERMISSIONS.COMPARISONS_SHARE)) {
      return `${formatMessage('Comparison.Share.ProvidedBy')} ${item.providedBy.company}`;
    }
    return `${formatMessage('Comparison.Share.ProvidedBy')} ${item.providedBy.name}`;
  }

  if (item.sharedWith && !!item.sharedWith.length) {
    const sharedUsers = item.sharedWith.filter(sharedUser => sharedUser.id !== user.id);

    const formattedUsers = enumerateArray(
      sharedUsers.map(sharedUser => (user && userHasPermission(user, PERMISSIONS.COMPARISONS_SHARE) ? sharedUser.company : sharedUser.name)),
      formatMessage('Common.And'),
    );

    return `${formatMessage('Comparison.Share.SharedWith')} ${formattedUsers}`;
  }

  return '';
};

const ComparisonCard = (props: Props) => {
  const {
    className,
    pinnedEditMode,
    item,
    user,
    readOnly,
    disableSaving,
    editedPortfolio,
    onCellClick,
    selectedCategories,
    onSelectItem,
    isSelected,
    comparison,
    markupMode,
    onSaveBenchmark,
    onSavePortfolio,
    getAdditionalCategoryStyles,
    disableStickyHeader,
    editBenchmarkMode,
    editedBenchmark,
    onEditBenchmarkCancel,
    hasStickyHeader,
    scrollbarContainerRef,
    showGroups,
    editGroups,
    locale,
    ...rest
  } = props;

  const allowExpandPortfolio = !readOnly && !pinnedEditMode && !markupMode && item.type === COMPARISON_ITEM_TYPES.PORTFOLIO;
  const itemIsExpanded = allowExpandPortfolio && item.isExpanded;
  const itemIsFX = item.isFX && !markupMode && !pinnedEditMode;
  const showEditBenchmark = !readOnly && editBenchmarkMode;

  const sharingInfo = user &&
    item.providedBy && {
      isMine: user.id === item.providedBy.id,
      label: getSharingLabel(user, item),
    };

  const classes = classNames('comparison-card', className, {
    shared: sharingInfo && !sharingInfo.isMine,
    benchmark: isBenchmark(item),
    selected: isSelected,
    'expanded-card': itemIsExpanded || itemIsFX,
  });

  const showCurrencies = !!user && !!comparison.showBaseCurrencies && userHasPermissionEnabled(user, PERMISSIONS.CURRENCY_CONVERSIONS);

  return (
    <div className={classes} role="row" onClick={() => onSelectItem(item)} id={`card-${item._id}`}>
      {showEditBenchmark && editedBenchmark && (
        <EditBenchmark readOnly={readOnly} onSave={onSaveBenchmark} onCancel={onEditBenchmarkCancel} showCurrencies={showCurrencies} item={item} {...rest} />
      )}

      {itemIsExpanded && (
        <ExpandedPortfolio
          user={user}
          item={item}
          readOnly={readOnly}
          sharingInfo={sharingInfo}
          onCellClick={onCellClick}
          selectedCategories={selectedCategories}
          comparison={comparison}
          markupMode={markupMode}
          getAdditionalCategoryStyles={getAdditionalCategoryStyles}
          disableStickyHeader={disableStickyHeader}
          onSavePortfolio={onSavePortfolio}
          editedPortfolio={editedPortfolio}
          isSelected={isSelected}
          hasStickyHeader={hasStickyHeader}
          scrollbarContainerRef={scrollbarContainerRef}
          showGroups={showGroups}
          editGroups={editGroups}
          locale={locale}
          {...rest}
        />
      )}

      {itemIsFX && (
        <ComparisonFXItem
          user={user}
          item={item}
          readOnly={readOnly}
          sharingInfo={sharingInfo}
          onCellClick={onCellClick}
          selectedCategories={selectedCategories}
          comparison={comparison}
          markupMode={markupMode}
          allowExpandPortfolio={allowExpandPortfolio}
          getAdditionalCategoryStyles={getAdditionalCategoryStyles}
          onSavePortfolio={onSavePortfolio}
          editedPortfolio={editedPortfolio}
          isSelected={isSelected}
          scrollbarContainerRef={scrollbarContainerRef}
          hasStickyHeader={hasStickyHeader}
          locale={locale}
          {...rest}
        />
      )}

      {!itemIsExpanded && !itemIsFX && !showEditBenchmark && (
        <ComparisonCardContent
          user={user}
          item={item}
          isFXSubItem={false}
          isExpandSubItem={false}
          showMenu
          readOnly={readOnly}
          sharingInfo={sharingInfo}
          onCellClick={onCellClick}
          selectedCategories={selectedCategories}
          comparison={comparison}
          markupMode={markupMode}
          allowExpandPortfolio={allowExpandPortfolio}
          getAdditionalCategoryStyles={getAdditionalCategoryStyles}
          onSavePortfolio={onSavePortfolio}
          isEdited={pinnedEditMode}
          disableStickyHeader={disableStickyHeader}
          disableSaving={disableSaving}
          editedPortfolio={editedPortfolio}
          isSelected={isSelected}
          scrollbarContainerRef={scrollbarContainerRef}
          hasStickyHeader={hasStickyHeader}
          showGroups={showGroups}
          editGroups={editGroups}
          locale={locale}
          updateComparisonItemCategory={props.updateComparisonItemCategory}
          {...rest}
        />
      )}
    </div>
  );
};

export default ComparisonCard;
